import { faCalendarAlt as farCalendarAlt } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useRef, useState } from "react";
import { Form, Button, InputGroup, Col, Row, Container } from "react-bootstrap";
import Datetime from "react-datetime";
import { useLang } from "../../common/hooks/useLang";
import "moment/locale/de";
import "moment/locale/en-gb";
import axios from "../../common/api/HttpClient";
import DateFormatHelper from "./DateFormatHelper";
import Select from 'react-select';
import { useLisaFormatter } from "../../common/hooks/useLisaFormatter";
import moment from "moment";

export default function DynamicFormControl(props) {
  if (props.additionalInfo["KE_DATENTYP"] === "C") {
    if (props.additionalInfo["DOMA_ID"] != null) {
      return DynamicSelectControl(props);
    } else {
      return DynamicTextControl(props);
    }
  } else if (props.additionalInfo["KE_DATENTYP"] === "T") {
    return DynamicMultilineTextControl(props);
  } else if (props.additionalInfo["KE_DATENTYP"] === "D") {
    return DynamicDateControl(props);
  } else if (props.additionalInfo["KE_DATENTYP"] === "N") {
    return DynamicNumberControl(props);
  } else if (props.additionalInfo["KE_DATENTYP"] === "J") {
    return DynamicYesNoControl(props);
  }
}

/**
 * Input "type=number" cannot be used here:
 * 1) Only dot is an allowed decimal separator
 * 2) We do not want the step up/step down buttons
 * 3) The input field increases the value on mouse wheel up/down.
 *    This collides with the whole page scrolling
 */
function DynamicNumberControl(props) {
  const { formatValue } = useLisaFormatter();

  const handleOnInput = (e) => {
    if (e.target.value.length > e.target.maxLength) {
      // Browsers do not use maxlength attribute of input type number,
      // so cut the value manually
      e.target.value = e.target.value.slice(0, e.target.maxLength);
    }
  }

  const handleOnKeyPress = (e) => {
    // Prevent other characters than digits,
    // if format does not contain a decimal separater
    if (props.additionalInfo["FORMAT"] !== null && 
        props.additionalInfo["FORMAT"].indexOf("D") === -1) {
      const charCode = (typeof e.which == "undefined") ? e.keyCode : e.which;
      const charStr = String.fromCharCode(charCode);
      if (new RegExp("\\D").test(charStr)) {
          e.preventDefault();
          return false;
      }
    }
  }

  const handleOnPaste = (e) => {
    // Prevent pasting, which contains at least one non-digit,
    // if format does not contain a decimal separater
    if (props.additionalInfo["FORMAT"] !== null && 
        props.additionalInfo["FORMAT"].indexOf("D") === -1) {
      const copiedText = e.clipboardData.getData("text");
      if (new RegExp("^[0-9]+$", "g").test(copiedText) === false) {
          e.preventDefault();
          return false;
      }
    }
  }

  const handleOnBlur = async(e) => {
    e.target.value = e.target.value?.trim();

    const result = await formatValue(
      props.additionalInfo["KE_DATENTYP"],
      props.additionalInfo["FORMAT"],
      e.target.value
    );
    
    let formattedValue;
    if (result?.success === "J") {
      formattedValue = result?.formattedValue;
    } else {
      formattedValue = "";
    }

    props.handleAdditionalInfoChanged(
      props.additionalInfo["LAYO_ID"],
      props.additionalInfo["LAYO_VERSION"],
      props.additionalInfo["LAPR_ID"],
      formattedValue
    );
  }

  return (
    <Container fluid="md" className="m-0 p-0">
      <Row className="align-items-center">
        <Col xs={12} md={4}>
          <Form.Label className="my-1">
            {props.additionalInfo["KE_PROMPT_ANZEIGEN"] === "N" ? "\u00A0" :
            props.additionalInfo["PROMPT"] + 
            (props.additionalInfo["KE_MUSS"] === "J" ? "*" : "")}
          </Form.Label>
        </Col>
        <Col xs={12} md={8}>
          <Form.Control
            type="text"
            className="dynamic-form-control"
            value={props.additionalInfo["VALUE"]}
            maxLength={props.additionalInfo["FORMAT"] ? props.additionalInfo["FORMAT"].length : "8"}
            required={props.additionalInfo["KE_MUSS"] === "J"}
            onChange={(e) =>
              props.handleAdditionalInfoChanged(
                props.additionalInfo["LAYO_ID"],
                props.additionalInfo["LAYO_VERSION"],
                props.additionalInfo["LAPR_ID"],
                e.target.value
              )
            }
            onInput={handleOnInput}
            onKeyPress={handleOnKeyPress}
            onPaste={handleOnPaste}
            onBlur={handleOnBlur}
          />
        </Col>
      </Row>
    </Container>
  );
}

function DynamicTextControl(props) {
  const handleOnInput = (e) => {
    if (props.additionalInfo["KE_GROSS"] === "J") {
      e.target.value = ("" + e.target.value).toUpperCase();
    }
  }

  const handleOnBlur = (e) => {
    e.target.value = e.target.value?.trim();
  }

  return (
    <Container fluid="md" className="m-0 p-0">
      <Row className="align-items-center">
        <Col xs={12} md={4}>
          <Form.Label className="my-1">
            {props.additionalInfo["KE_PROMPT_ANZEIGEN"] === "N" ? "\u00A0" :
            props.additionalInfo["PROMPT"] + 
            (props.additionalInfo["KE_MUSS"] === "J" ? "*" : "")}
          </Form.Label>
        </Col>
        <Col xs={12} md={8}>
          <Form.Control
            type="text"
            className="dynamic-form-control"
            value={props.additionalInfo["VALUE"]}
            maxLength={props.additionalInfo["FORMAT"]}
            required={props.additionalInfo["KE_MUSS"] === "J"}
            onChange={(e) =>
              props.handleAdditionalInfoChanged(
                props.additionalInfo["LAYO_ID"],
                props.additionalInfo["LAYO_VERSION"],
                props.additionalInfo["LAPR_ID"],
                e.target.value
              )
            }
            onInput={handleOnInput}
            onBlur={handleOnBlur}
          />
        </Col>
      </Row>
    </Container>
  );
}

function DynamicMultilineTextControl(props) {
  const handleOnInput = (e) => {
    if (props.additionalInfo["KE_GROSS"] === "J") {
      e.target.value = ("" + e.target.value).toUpperCase();
    }
  }

  const handleOnBlur = (e) => {
    e.target.value = e.target.value?.trim();
  }

  return (
    <Container fluid="md" className="m-0 p-0">
      <Row className="align-items-center">
        <Col xs={12} md={4}>
          <Form.Label className="my-1">
            {props.additionalInfo["KE_PROMPT_ANZEIGEN"] === "N" ? "\u00A0" :
            props.additionalInfo["PROMPT"] + 
            (props.additionalInfo["KE_MUSS"] === "J" ? "*" : "")}
          </Form.Label>
        </Col>
        <Col xs={12} md={8}>
          <Form.Control
            as="textarea"
            rows="4"
            style={{height: "auto"}}
            className="dynamic-form-control"
            value={props.additionalInfo["VALUE"]}
            maxLength={props.additionalInfo["FORMAT"]}
            required={props.additionalInfo["KE_MUSS"] === "J"}
            onChange={(e) =>
              props.handleAdditionalInfoChanged(
                props.additionalInfo["LAYO_ID"],
                props.additionalInfo["LAYO_VERSION"],
                props.additionalInfo["LAPR_ID"],
                e.target.value
              )
            }
            onInput={handleOnInput}
            onBlur={handleOnBlur}
          />
        </Col>
      </Row>
    </Container>
  );
}

function DynamicSelectControl(props) {
  const { currentLanguage } = useLang();

  const [domainValues, setDomainValues] = useState([]);
  const [mappedValues, setMappedValues] = useState([]);
  const [selectedOption, setSelectedOption] = useState();

  const mapFixedDomainValue = useCallback((value) => {
    // Internal value = P1 (pdowe.min_wert)
    // Visible value = P2 (pdowe.bezeichnung)
    return {
      value: value["P1"],
      label: value["P2"]
    }
  }, []);

  const mapDynamicDomainValue = useCallback((value) => {
    let otherParameters = [
      value["P2"],
      value["P3"],
      value["P4"],
      value["P5"],
      value["P6"]
    ].filter(x => typeof x === "string" && x.trim().length > 0)
     .join(", ");

    if (otherParameters) {
      otherParameters = ` (${otherParameters})`;
    }

    // Internal value = P1
    // Visible value = P1 plus optionally P2-P6
    return {
      value: value["P1"],
      label: value["P1"] + otherParameters
    }
  }, []);

  const mapDomainValue = useCallback((value) => {
    let mappedValue = {};

    if (value["TYP"] === "W") {
      mappedValue = mapFixedDomainValue(value);
    } else {
      mappedValue = mapDynamicDomainValue(value);
    }

    return mappedValue;
  }, [mapFixedDomainValue, mapDynamicDomainValue]);

  const mapDomainValues = useCallback((values) => {
    let mappedValues = [];
    for (const value of values) {
      mappedValues.push(mapDomainValue(value));
    }

    return mappedValues;
  }, [mapDomainValue]);

  useEffect(() => {
    let notInterrupted = true;

    if (props.additionalInfo.DOMA_ID != null) {
      axios({
        method: "get",
        url: `/domain/${props.additionalInfo.DOMA_ID}`,
        params: { 
          sprache: currentLanguage,
          param1: props.additionalInfo.DOMAIN_PARAM_1,
          param2: props.additionalInfo.DOMAIN_PARAM_2,
          param3: props.additionalInfo.DOMAIN_PARAM_3,
          param4: props.additionalInfo.DOMAIN_PARAM_4,
          param5: props.additionalInfo.DOMAIN_PARAM_5
        }
      })
        .then((response) => {
          if (notInterrupted) {
            setDomainValues(response.data.data);
          }
        })
        .catch((error) => {
          setDomainValues([]);
          console.error(error);
        });
    }

    // If clean-up function executes, the axios call was interrupted
    // and the results can not be rendered in the dom anymore.
    // This may happen, if the user navigates back during the call.
    return () => {
      notInterrupted = false;
    }

  }, [props.additionalInfo.DOMA_ID,
      props.additionalInfo.DOMAIN_PARAM_1,
      props.additionalInfo.DOMAIN_PARAM_2,
      props.additionalInfo.DOMAIN_PARAM_3,
      props.additionalInfo.DOMAIN_PARAM_4,
      props.additionalInfo.DOMAIN_PARAM_5,
      currentLanguage]);

  useEffect(() => {
    setMappedValues(mapDomainValues(domainValues));
  }, [mapDomainValues, domainValues]);

  useEffect(() => {
    if (props.additionalInfo.VALUE == null ||
        props.additionalInfo.VALUE === "") {
      setSelectedOption(null);
    }
    else {
      for (const domainValue of domainValues) {
        if (props.additionalInfo.VALUE === domainValue["P1"]) {
          setSelectedOption(mapDomainValue(domainValue));
        }
      }
    }
  }, [props.additionalInfo.VALUE, domainValues, mapDomainValue]);

  const handleOnChange = (e) => {
    // Bring back to valid state
    setInvalid(false);

    props.handleAdditionalInfoChanged(
      props.additionalInfo["LAYO_ID"],
      props.additionalInfo["LAYO_VERSION"],
      props.additionalInfo["LAPR_ID"],
      e == null ? null : e.value
    );
  }

  const [invalid, setInvalid] = useState(false);
  const handleOnInvalid = (e) => {
    // This adds a red border to signal an invalid value
    setInvalid(true);
  }

  return (
    <Container fluid="md" className="m-0 p-0">
      <Row className="align-items-center">
        <Col xs={12} md={4}>
          <Form.Label className="my-1">
            {props.additionalInfo["KE_PROMPT_ANZEIGEN"] === "N" ? "\u00A0" :
            props.additionalInfo["PROMPT"] + 
            (props.additionalInfo["KE_MUSS"] === "J" ? "*" : "")}
          </Form.Label>
        </Col>
        <Col xs={12} md={8}>
          <RequiredSelect
            SelectComponent={Select}
            className={invalid ? "dynamic-select-container dynamic-form-control-invalid" : "dynamic-select-container"}
            classNamePrefix="dynamic-select"
            placeholder=""
            value={selectedOption}
            onChange={handleOnChange}
            options={mappedValues}
            noOptionsMessage={() => ""}
            isSearchable={true}
            isClearable={true}
            required={props.additionalInfo["KE_MUSS"] === "J"}
            onInvalid={(e) => handleOnInvalid(e)}
          >
          </RequiredSelect>
        </Col>
      </Row>
    </Container>
  );
}

function DynamicDateControl(props) {
  const { currentLanguage } = useLang();
  const [isOpened, setOpened] = useState(false);
  const { formatValue } = useLisaFormatter();
  const { additionalInfo, handleAdditionalInfoChanged } = props;
  const {
    FORMAT,
    KE_DATENTYP,
    KE_GROSS,
    LAPR_ID,
    LAYO_ID,
    LAYO_VERSION,
    VALUE,
  } = additionalInfo;

  const [jsFormat, setJsFormat] = useState(
    DateFormatHelper.fromDbFormatToJsFormat(FORMAT)
  );

  const handleOnInput = useCallback((e) => {
    if (KE_GROSS === "J") {
      e.target.value = ("" + e.target.value).toUpperCase();
    }
  }, [KE_GROSS]);

  const handleOnBlur = useCallback(async(e) => {
    e.target.value = e.target.value?.trim();

    const result = await formatValue(
      KE_DATENTYP,
      FORMAT,
      e.target.value
    );
    
    let formattedValue;
    if (result?.success === "J") {
      formattedValue = result?.formattedValue;
    } else {
      formattedValue = "";
    }

    inputRef.current.value = formattedValue;

    handleAdditionalInfoChanged(
      LAYO_ID,
      LAYO_VERSION,
      LAPR_ID,
      formattedValue
    );

  }, [
    FORMAT,
    KE_DATENTYP,
    LAPR_ID,
    LAYO_ID,
    LAYO_VERSION,
    handleAdditionalInfoChanged,
    formatValue,
  ]);

  const [inputProps, setInputProps] = useState({
    key: "key-" + LAPR_ID,
    onInput: handleOnInput,
    onBlur: handleOnBlur,
    additionalInfo: additionalInfo
  });

  useEffect(() => {
    setJsFormat(
      DateFormatHelper.fromDbFormatToJsFormat(FORMAT)
    );

    setInputProps({
      key: "key-" + LAPR_ID,
      onInput: handleOnInput,
      onBlur: handleOnBlur,
      additionalInfo: additionalInfo
    });
  }, [FORMAT, LAPR_ID, additionalInfo, handleOnInput, handleOnBlur]);

  const inputRef = useRef(VALUE);

  const renderInput = (props, openCalendar, closeCalendar) => {

    // Destructure and do not use "props.value" and "props.onChange" here!
    // There is a Bug in react-datetime which prevents to clear the input value e.g. in onBlur.
    // Note: By disabling "onChange" the popup calender does not synchronize 
    // with the current user input anymore, but we accept this behavior.
    // https://github.com/arqex/react-datetime/issues/640
    // https://stackoverflow.com/questions/46053202/how-to-clear-the-value-entered-in-react-datetime
    const { additionalInfo, value, onChange, ...otherProps } = props;

    function toggleCalendar() {
      if (isOpened === true) {
        closeCalendar();
        setOpened(false);
      } else {
        openCalendar();
        setOpened(true);
      }
    }

    return (
      <InputGroup className="position-relative">
        <Form.Control 
          {...otherProps}
          className="dynamic-form-control pe-5"
          required={additionalInfo?.KE_MUSS === "J"}
          ref={inputRef}
          defaultValue={additionalInfo?.VALUE}
        />
        <Button
          onClick={toggleCalendar}
          className="bg-transparent border-0 position-absolute end-0"
          style={{ zIndex: 3 }}
        >
          <FontAwesomeIcon icon={farCalendarAlt} />
        </Button>
      </InputGroup>
    );
  }

  const handleCalendarClosed = () => {
    setOpened(false);
  };

  /**
   * @param {*} value The callback receives the selected moment object as only parameter, 
   *            if the date in the input is valid. If the date in the input is not valid,
   *            the callback receives the value of the input (a string).
   */
  const handleDateChanged = (value) => {
    let dateAsString;
    if (value && moment.isMoment(value)) {
      dateAsString = value.format(jsFormat.fullFormat);
    } else {
      // Invalid date value -> Apply empty value instead
      // Formatted value will also be applied in onBlur
      dateAsString = "";
    }

    inputRef.current.value = dateAsString;

    props.handleAdditionalInfoChanged(
      props.additionalInfo["LAYO_ID"],
      props.additionalInfo["LAYO_VERSION"],
      props.additionalInfo["LAPR_ID"],
      dateAsString
    );
  };

  return (
    <Container fluid="md" className="m-0 p-0">
      <Row className="align-items-center">
        <Col xs={12} md={4}>
          <Form.Label className="my-1">
            {props.additionalInfo["KE_PROMPT_ANZEIGEN"] === "N" ? "\u00A0" :
            props.additionalInfo["PROMPT"] + 
            (props.additionalInfo["KE_MUSS"] === "J" ? "*" : "")}
          </Form.Label>
        </Col>
        <Col xs={12} md={8}>
          <Datetime
            renderInput={renderInput}
            locale={currentLanguage === "en" ? "en-gb" : currentLanguage}
            dateFormat={jsFormat.dateFormat}
            timeFormat={jsFormat.timeFormat}
            closeOnSelect={true}
            // Value is still needed together with the ref
            // to autmatically mark the current date in the popup calendar
            // after onBlur. Direct synchronizing in onChange does not work anymore.
            value={props.additionalInfo["VALUE"]}
            onClose={handleCalendarClosed}
            inputProps={inputProps}
            onChange={handleDateChanged}
          />
        </Col>
      </Row>
    </Container>
  );
}

function DynamicYesNoControl(props) {
  const { currentLanguage } = useLang();

  const [domainValues, setDomainValues] = useState([]);
  const [mappedValues, setMappedValues] = useState([]);
  const [selectedOption, setSelectedOption] = useState();

  const mapDomainValue = useCallback((value) => {
    // Internal value = P1 (pdowe.min_wert)
    // Visible value = P2 (pdowe.bezeichnung)
    return {
      value: value["P1"],
      label: value["P2"]
    }
  }, []);

  const mapDomainValues = useCallback((values) => {
    let mappedValues = [];
    for (const value of values) {
      mappedValues.push(mapDomainValue(value));
    }

    return mappedValues;
  }, [mapDomainValue]);
  useEffect(() => {
    let notInterrupted = true;

    axios({
      method: "get",
      url: "/domain/LLIMS.JA_NEIN_STRING",
      params: { sprache: currentLanguage }
    })
      .then((response) => {
        if (notInterrupted) {
          setDomainValues(response.data.data);
        }
      })
      .catch((error) => {
        setDomainValues([]);
        console.error(error);
      });

    // If clean-up function executes, the axios call was interrupted
    // and the results can not be rendered in the dom anymore.
    // This may happen, if the user navigates back during the call.
    return () => {
      notInterrupted = false;
    }
  }, [currentLanguage]);

  useEffect(() => {
    setMappedValues(mapDomainValues(domainValues));
  }, [mapDomainValues, domainValues]);

  useEffect(() => {
    if (props.additionalInfo.VALUE == null ||
        props.additionalInfo.VALUE === "") {
      setSelectedOption(null);
    }
    else {
      for (const domainValue of domainValues) {
        if (props.additionalInfo.VALUE === domainValue["P1"]) {
          setSelectedOption(mapDomainValue(domainValue));
        }
      }
    }
  }, [props.additionalInfo.VALUE, domainValues, mapDomainValue]);

  const handleOnChange = (e) => {
    // Bring back to valid state
    setInvalid(false);

    props.handleAdditionalInfoChanged(
      props.additionalInfo["LAYO_ID"],
      props.additionalInfo["LAYO_VERSION"],
      props.additionalInfo["LAPR_ID"],
      e == null ? null : e.value
    );
  }

  const [invalid, setInvalid] = useState(false);
  const handleOnInvalid = (e) => {
    // This adds a red border to signal an invalid value
    setInvalid(true);
  }

  return (
    <Container fluid="md" className="m-0 p-0">
      <Row className="align-items-center">
        <Col xs={12} md={4}>
          <Form.Label className="my-1">
            {props.additionalInfo["KE_PROMPT_ANZEIGEN"] === "N" ? "\u00A0" :
            props.additionalInfo["PROMPT"] + 
            (props.additionalInfo["KE_MUSS"] === "J" ? "*" : "")}
          </Form.Label>
        </Col>
        <Col xs={12} md={8}>
          <RequiredSelect
            SelectComponent={Select}
            className={invalid ? "dynamic-select-container dynamic-form-control-invalid" : "dynamic-select-container"}
            classNamePrefix="dynamic-select"
            placeholder=""
            value={selectedOption}
            onChange={handleOnChange}
            options={mappedValues}
            noOptionsMessage={() => ""}
            isSearchable={false}
            isClearable={true}
            required={props.additionalInfo["KE_MUSS"] === "J"}
            onInvalid={(e) => handleOnInvalid(e)}
          >
          </RequiredSelect>
        </Col>
      </Row>
    </Container>
  );
}

/**
 * Extends the react-select to use "required" attribute.
 * 
 * @see https://codesandbox.io/s/react-select-v2-required-input-3xvvb?fontsize=14&file=/src/FixRequiredSelect.js
 */
function RequiredSelect({ SelectComponent, required, isDisabled, onInvalid, ...props }) {

  const [value, setValue] = useState(() => {
    return props.value || "";
  });

  const selectRef = useRef(null);

  const onChange = (value, actionMeta) => {
    props.onChange(value, actionMeta);
    setValue(value);
  };

  const getValue = () => {
    // "!=" is correct here, so disable this rule for the next line
    // eslint-disable-next-line eqeqeq
    if (props.value != undefined) {
      return props.value;
    } else {
      return value || "";
    }
  };

  return (
    <>
      <SelectComponent
        {...props}
        ref={selectRef}
        onChange={onChange}
      />
      {!isDisabled && (
        <input
          tabIndex={-1}
          autoComplete="off"
          style={{
            opacity: 0,
            width: 0,
            height: 0,
            position: "absolute"
          }}
          value={getValue()}
          onChange={() => {
            // Do nothing / No operation
          }}
          onFocus={() => selectRef.focus()}
          required={required}
          onInvalid={onInvalid}
        />
      )}
  </>
  )
}
